import type { HttpErrorResponse } from '@angular/common/http';

import type { DisqualifyReason, DisqualifyReasonCreate } from '@core/disqualify-reasons-types';
import { createEntityCollectionStore, getEntityCollectionActions } from '@core/ngrx-legacy';

import { DisqualifyReasonsAutomationsSelectors } from './automations/disqualify-reasons-automations.store';

const idSelector = ({ id }: { id: number | string }) => id;
const { actions, namespace, reducer, selectors } = createEntityCollectionStore<DisqualifyReason>()(
  'disqualify-reason',
  {
    idSelector,
    references: makeRef => [
      makeRef('automationIds', 'automations', automationIds =>
        DisqualifyReasonsAutomationsSelectors.byIds(automationIds || []),
      ),
    ],
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        createOnePessimistic: (
          state,
          payload: { data: DisqualifyReasonCreate; correlationId: string },
        ) => defaultActions.createOnePessimistic(state, payload),

        updateOnePessimistic: (state, payload: { data: DisqualifyReason }) =>
          defaultActions.updateOnePessimistic(state, payload),

        removeOnePessimistic: (
          state,
          payload: { data: number; fallbackDisqualifyReasonId?: number },
        ) => defaultActions.removeOnePessimistic(state, payload),

        updatePositions: (state, payload: { data: DisqualifyReason[] }) =>
          adapter.markAsPending(state, { entities: payload.data.map(idSelector), scope: null }),
        updatePositionsSuccess: (state, payload) =>
          adapter.markAsLoaded(state, { entities: payload.data.map(idSelector), scope: null }),
        updatePositionsFailed: (
          state,
          payload: { error: HttpErrorResponse; data: DisqualifyReason[] },
        ) =>
          adapter.setError(state, payload.error, {
            entities: payload.data.map(idSelector),
            scope: null,
          }),
      };
    },
  },
);

const DisqualifyReasonsActions = <
  Pick<
    typeof actions,
    | 'createOnePessimistic'
    | 'createOnePessimisticSuccess'
    | 'createOnePessimisticFailed'
    | 'fetchCollection'
    | 'fetchCollectionSuccess'
    | 'fetchCollectionFailed'
    | 'updateOnePessimistic'
    | 'updateOnePessimisticSuccess'
    | 'updateOnePessimisticFailed'
    | 'removeOnePessimistic'
    | 'removeOnePessimisticSuccess'
    | 'removeOnePessimisticFailed'
    | 'updatePositions'
    | 'updatePositionsSuccess'
    | 'updatePositionsFailed'
  >
>actions;

export {
  DisqualifyReasonsActions,
  namespace as DisqualifyReasonsNamespace,
  reducer as DisqualifyReasonsReducer,
  selectors as DisqualifyReasonsSelectors,
};
